import React, { useEffect } from 'react'

import { useRightPanel } from 'hooks/usePinnedSidecar'
import { useStopBodyScroll } from 'hooks/useScrollEffects'
import { useSwipeEffects } from 'hooks/useSwipeEffects'
import useKeylistener from 'hooks/useKeylistener'

import PhatDropdown from 'components/formElements/PhatDropdown'
import ManaCurves from 'components/deckPage/charts/ManaCurves'
import ColorProductionVsGeneration from 'components/deckPage/charts/ColorProductionVsGeneration'
import Probability from 'components/deckPage/RightPanel/Probability'
import PhatButton from 'components/formElements/PhatButton'
import Icon from 'components/elements/Icon'
import QuantityTables from 'components/deckPage/charts/QuantityTables'
import LegacyStats from 'components/deckPage/RightPanel/LegacyStats'

import { clamp } from 'utils/numbers'

import styles from './rightPanel.module.scss'

const RightPanelV2 = () => {
  const [rightPanelStatus, setRightPanelStatus] = useRightPanel()

  const setStopScroll = useStopBodyScroll(rightPanelStatus.open)

  const panel = useSwipeEffects('horizontal', {
    checkCanStartSwipe: () =>
      rightPanelStatus.open && !rightPanelStatus.locked && document.documentElement.clientWidth < 600,
    onTouchMove: (offset, direction) => {
      const screenWidth = document.documentElement.clientWidth

      if (!panel.current) return
      if (!rightPanelStatus.open || rightPanelStatus.locked) return

      if (direction === 'right') {
        panel.current.style.transform = `translateX(${clamp(0, 100, (offset / screenWidth) * 1.4 * 100)}dvw)`
        panel.current.style.transition = 'unset'
      }
    },
    onTouchEnd: (offset, _position, speed, direction) => {
      if (!panel.current) return

      const panelOpen = rightPanelStatus.open && !rightPanelStatus.locked
      const screenWidth = document.documentElement.clientWidth
      const swipeSize = (offset / screenWidth) * 1.4 * 100

      if (panelOpen && direction === 'right') {
        if (swipeSize > 10 || speed > 0.5) setRightPanelStatus({ ...rightPanelStatus, open: false })
        else setRightPanelStatus({ ...rightPanelStatus, open: true })

        panel.current.style.transition = ''
        panel.current.style.transform = ''
      }
    },
  })

  useKeylistener(
    e => {
      if (!rightPanelStatus.open || rightPanelStatus.locked) return

      if (e.key === 'Escape') {
        e.preventDefault()

        setRightPanelStatus({ ...rightPanelStatus, open: false })
      }
    },
    [rightPanelStatus],
  )

  useEffect(() => {
    if (!panel.current) return

    if (!rightPanelStatus.open) panel.current.removeAttribute('style')
    if (rightPanelStatus.open && panel.current) panel.current.style.transform = 'translateX(0)'

    setStopScroll(rightPanelStatus.open && !rightPanelStatus.locked)
  }, [rightPanelStatus])

  return (
    <>
      <div
        className={`${styles.dimmer} ${rightPanelStatus.open && !rightPanelStatus.locked ? styles.dimmerEnabled : ''}`}
        onClick={() => setRightPanelStatus({ ...rightPanelStatus, open: false })}
      />

      <div
        ref={panel}
        className={`
          ${rightPanelStatus.locked ? styles.lockedContainer : styles.fixedContainer} 
          ${rightPanelStatus.open && !rightPanelStatus.locked ? styles.open : ''}
      `}>
        <div className={styles.content}>
          <div className={styles.controls}>
            <PhatDropdown
              value={rightPanelStatus.type}
              className={styles.viewDropdownContainer}
              triggerWrapperClassName={styles.viewDropdown}
              options={[
                {
                  label: 'Condensed view',
                  onClick: () => setRightPanelStatus({ ...rightPanelStatus, type: 'condensed' }),
                  id: 'condensed',
                },
                { type: 'spacer' },
                {
                  label: 'Colors',
                  onClick: () => setRightPanelStatus({ ...rightPanelStatus, type: 'colors' }),
                  id: 'colors',
                },
                {
                  label: 'Mana Curve',
                  onClick: () => setRightPanelStatus({ ...rightPanelStatus, type: 'manaCurve' }),
                  id: 'manaCurve',
                },
                {
                  label: 'Probability',
                  onClick: () => setRightPanelStatus({ ...rightPanelStatus, type: 'probability' }),
                  id: 'probability',
                },
                {
                  label: 'Quantities',
                  onClick: () => setRightPanelStatus({ ...rightPanelStatus, type: 'qunatities' }),
                  id: 'qunatities',
                },
              ]}
            />

            <PhatButton
              onClick={() => setRightPanelStatus({ ...rightPanelStatus, locked: !rightPanelStatus.locked })}
              className={styles.pinToggle}>
              <Icon name="columns" />
              {rightPanelStatus.locked ? 'Unpin' : 'Pin'}
            </PhatButton>
          </div>

          {rightPanelStatus.type === 'colors' && <ColorProductionVsGeneration />}
          {rightPanelStatus.type === 'probability' && <Probability />}
          {rightPanelStatus.type === 'manaCurve' && <ManaCurves />}
          {rightPanelStatus.type === 'qunatities' && <QuantityTables />}
          {rightPanelStatus.type === 'condensed' && <LegacyStats />}
        </div>
      </div>
    </>
  )
}

export default RightPanelV2
